/**
 * offlineDbService.js
 * Servicio wrapper de IndexedDB para almacenamiento local y contingencia offline.
 */

const DB_NAME = 'TiendaOfflineDB';
const DB_VERSION = 1;

class OfflineDbService {
    constructor() {
        this.db = null;
    }

    /**
     * Inicializa la base de datos IndexedDB.
     */
    async openDb() {
        if (this.db) {
            return this.db;
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = (event) => {
                const db = event.target.result;

                // Catálogo de productos para POS offline
                if (!db.objectStoreNames.contains('products')) {
                    const productStore = db.createObjectStore('products', { keyPath: 'id' });
                    productStore.createIndex('name', 'name', { unique: false });
                    productStore.createIndex('barcode', 'barcode', { unique: false });
                    productStore.createIndex('sku', 'sku', { unique: false });
                }

                // Clientes cacheados
                if (!db.objectStoreNames.contains('customers')) {
                    const customerStore = db.createObjectStore('customers', { keyPath: 'id' });
                    customerStore.createIndex('name', 'name', { unique: false });
                    customerStore.createIndex('phone', 'phone', { unique: false });
                }

                // Cola de transacciones/pedidos pendientes de sincronizar
                if (!db.objectStoreNames.contains('offline_orders')) {
                    const orderStore = db.createObjectStore('offline_orders', { keyPath: 'client_uuid' });
                    orderStore.createIndex('sync_status', 'sync_status', { unique: false });
                    orderStore.createIndex('created_at', 'created_at', { unique: false });
                }
            };

            request.onsuccess = (event) => {
                this.db = event.target.result;
                resolve(this.db);
            };

            request.onerror = (event) => {
                console.error("IndexedDB open error:", event.target.error);
                reject(event.target.error);
            };
        });
    }

    // ==========================================
    // PRODUCTOS (CATÁLOGO OFFLINE)
    // ==========================================

    async saveProducts(productsList) {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['products'], 'readwrite');
            const store = transaction.objectStore('products');

            productsList.forEach((prod) => {
                store.put(prod);
            });

            transaction.oncomplete = () => resolve(true);
            transaction.onerror = (e) => reject(e.target.error);
        });
    }

    async getAllProducts() {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['products'], 'readonly');
            const store = transaction.objectStore('products');
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result || []);
            request.onerror = (e) => reject(e.target.error);
        });
    }

    async findProductByBarcode(barcode) {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['products'], 'readonly');
            const store = transaction.objectStore('products');
            const index = store.index('barcode');
            const request = index.get(barcode);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = (e) => reject(e.target.error);
        });
    }

    // ==========================================
    // COLA DE ÓRDENES OFFLINE
    // ==========================================

    /**
     * Encola una orden tomada sin conexión.
     */
    async enqueueOrder(orderPayload) {
        const db = await this.openDb();
        const client_uuid = orderPayload.client_uuid || (crypto.randomUUID ? crypto.randomUUID() : 'offline-' + Date.now());
        const record = {
            ...orderPayload,
            client_uuid: client_uuid,
            sync_status: 'pending', // 'pending' | 'syncing' | 'synced' | 'failed'
            created_at: new Date().toISOString(),
            retry_count: 0
        };

        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['offline_orders'], 'readwrite');
            const store = transaction.objectStore('offline_orders');
            const request = store.put(record);

            request.onsuccess = () => resolve(record);
            request.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Obtiene todas las órdenes pendientes de sincronizar.
     */
    async getPendingOrders() {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['offline_orders'], 'readonly');
            const store = transaction.objectStore('offline_orders');
            const index = store.index('sync_status');
            const request = index.getAll('pending');

            request.onsuccess = () => resolve(request.result || []);
            request.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Marca una orden como sincronizada exitosamente con el ID devuelto por el servidor.
     */
    async markOrderSynced(client_uuid, serverOrderId) {
        const db = await this.openDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(['offline_orders'], 'readwrite');
            const store = transaction.objectStore('offline_orders');
            const getReq = store.get(client_uuid);

            getReq.onsuccess = () => {
                const data = getReq.result;
                if (data) {
                    data.sync_status = 'synced';
                    data.server_order_id = serverOrderId;
                    data.synced_at = new Date().toISOString();
                    store.put(data);
                }
                resolve(true);
            };
            getReq.onerror = (e) => reject(e.target.error);
        });
    }

    /**
     * Cuenta cuántas órdenes quedan pendientes de sincronización.
     */
    async countPendingOrders() {
        const orders = await this.getPendingOrders();
        return orders.length;
    }
}

export default new OfflineDbService();
